import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { ROUTE_TYPE_ICONS } from "../../lib/routeTypeDisplay";
import { zIndexes } from "../../lib/zIndexes";
import { AnchoredPopover } from "../AnchoredPopover";
import { ConnectorAvatars } from "../ConnectorAvatars";
import { Icon, type IconName } from "../Icon";
import { Text } from "../Text";
import {
  getLastTooltipCloseTime,
  setLastTooltipCloseTime,
  TOOLTIP_DELAY,
  WARM_PERIOD,
} from "../Tooltip";
import type { StepData } from "../Workflow";

const CARD_WIDTH = 240;
const EDGE_GUARD = 140;

const Popover = styled(AnchoredPopover)`
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  --text-secondary: var(--tooltip-fg-subtle);
  /* The surface is pinned dark in both modes, so tell theme-aware connector
     logos (light-dark() SVGs) to paint their dark-surface variant here. */
  color-scheme: dark;
  box-shadow: var(--elevation-low);
  padding: 8px 10px;
  z-index: ${zIndexes.tooltip};
  width: ${CARD_WIDTH}px;
  text-align: left;
  font-size: 10px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 6px;

  .counter {
    background: var(--tooltip-highlight);
    color: var(--tooltip-fg);
    font-size: 9px;
    font-weight: 500;
    line-height: 12px;
    letter-spacing: -1%;
    display: inline-flex;
    min-width: 16px;
    width: fit-content;
    border-radius: 10px;
    padding: 2px 6px;
    justify-content: center;
    flex-shrink: 0;
  }
`;

type ConnectedAvatar = React.ComponentProps<
  typeof ConnectorAvatars
>["connected"];
type DisconnectedAvatar = NonNullable<
  React.ComponentProps<typeof ConnectorAvatars>["disconnected"]
>;

type Props = {
  step: StepData;
  hovered: boolean;
  anchorName: string;
  anchorRef: React.RefObject<HTMLElement | null>;
  incomingFromStepIds: string[];
  copyableEmail: string | null;
  isChanged: boolean;
  connected: ConnectedAvatar;
  disconnected: DisconnectedAvatar;
};

// The compact card tiers hide the full card's counter, badges, logos, and
// description, so hovering a card surfaces them in a flyout above the card
// (flipping when the viewport is in the way). It mirrors the full card's
// bottom strip — count pill, icon badges, then right-aligned logos.
export function StepHoverCard({
  step,
  hovered,
  anchorName,
  anchorRef,
  incomingFromStepIds,
  copyableEmail,
  isChanged,
  connected,
  disconnected,
}: Props) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hovered) {
      const show = () => {
        setPosition(computePosition(anchorRef.current));
        setOpen(true);
      };
      if (Date.now() - getLastTooltipCloseTime() < WARM_PERIOD) {
        show();
        return;
      }
      openTimerRef.current = setTimeout(show, TOOLTIP_DELAY);
      return () => {
        if (openTimerRef.current) {
          clearTimeout(openTimerRef.current);
          openTimerRef.current = null;
        }
      };
    }
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setOpen((wasOpen) => {
      if (wasOpen) setLastTooltipCloseTime(Date.now());
      return false;
    });
  }, [hovered, anchorRef]);

  const hasIncoming = incomingFromStepIds.length > 0;
  const badges: IconName[] = [
    incomingFromStepIds.length > 1 ? "input" : null,
    step.publicRoute && !hasIncoming ? "link-2" : null,
    copyableEmail && !hasIncoming ? "at-sign" : null,
    step.schedule?.cronExpression ? "clock" : null,
    step.hasConflicts ? "merge" : null,
    step.hasOutput || step.routeType
      ? step.routeType
        ? ROUTE_TYPE_ICONS[step.routeType]
        : "earth"
      : null,
    step.hasStorage ? "box" : null,
    isChanged ? "traffic-cone" : null,
  ].filter((n): n is IconName => n !== null);

  if (!open) return null;

  const hasLogos = connected.length > 0 || disconnected.length > 0;

  return (
    <Popover isOpen target={anchorName} style={position} trigger={() => null}>
      <Text variant="text-small-md">{step.name}</Text>
      {step.readmeDescription && (
        <Text variant="text-small" color="var(--tooltip-fg-subtle)">
          {step.readmeDescription}
        </Text>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span className="counter">{step.executionCount.toLocaleString()}</span>
        {badges.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
            }}
          >
            {badges.map((name) => (
              <Icon
                key={name}
                name={name}
                size={12}
                color="var(--tooltip-fg-muted)"
              />
            ))}
          </div>
        )}
        {hasLogos && (
          <ConnectorAvatars
            flat
            collapseToFit
            align="end"
            connected={connected}
            disconnected={disconnected}
          />
        )}
      </div>
    </Popover>
  );
}

function computePosition(anchor: HTMLElement | null): React.CSSProperties {
  const styles: Record<string, string> = {};
  if (!anchor) return styles;
  const rect = anchor.getBoundingClientRect();

  if (rect.top < EDGE_GUARD) {
    styles.top = "anchor(bottom)";
    styles.marginTop = "6px";
  } else {
    styles.bottom = "anchor(top)";
    styles.marginBottom = "6px";
  }

  if (window.innerWidth - rect.left < CARD_WIDTH + 20) {
    styles.right = "anchor(right)";
  } else {
    styles.left = "anchor(left)";
  }

  return styles as React.CSSProperties;
}
